<!--
  Layout structure (single flex-nowrap row):

    [LEFT items] [spacer flex:1] [CENTER items] [More button?] [spacer flex:1] [RIGHT items]

  The two flex:1 spacers act as elastic gutters:
  - When all items fit, leftover space is split equally between the two spacers,
    pushing each group to its edge (left / center / right).
  - When CENTER is empty, the two spacers merge into one contiguous space, but
    left and right groups still stay at their edges.
  - The More button sits inside the center section (between the spacers) so it
    never displaces pinned edge items like LOGO or AUTH.

  Overflow is controlled exclusively by JS (visibleItems / hiddenItems signals).
  flex-nowrap prevents the browser from wrapping items before JS can intervene.

  During measurement (_measuring = true), `visibility: hidden` is applied to the
  whole row so users never see the "all items rendered" flash.
-->
<div
  class="header-overflow-row__items tw-flex tw-flex-nowrap tw-items-center tw-w-full tw-h-full tw-min-w-0"
  [class.header-overflow-row__items--measuring]="_measuring()"
  [style.--overflow-row-items-gap]="isMobile() ? 'var(--spacing-2)' : null"
>
  <!-- LEFT items -->
  @for (item of _visibleLeftItems(); track item._id) {
    <div #itemEl class="header-overflow-row__item-wrapper tw-inline-flex tw-shrink-0 tw-items-center tw-self-stretch">
      <app-menu-top-bar-item
        [item]="item"
        [isActive]="activeMap().get(item._id)"
        [routerPath]="_routerPaths().get(item._id) ?? null"
        [isCasinoOpened]="isCasinoOpened()"
        [isLightTheme]="isLightTheme()"
        [isLoggedIn]="isLoggedIn()"
        [showThemeTooltip]="showThemeTooltip()"
        [hideButtonLightMode]="hideButtonLightMode()"
        [showCrmButton]="showCrmButton()"
        [labelClass]="labelClass()"
        [inSubheader]="inSubheader()"
        (itemClick)="itemClick.emit($event)"
        (themeToggle)="themeToggle.emit()"
      >
        @if (_loginItemIds().has(item._id) && loginTemplate()) {
          <ng-container *ngTemplateOutlet="loginTemplate(); context: { item: item }"></ng-container>
        }
      </app-menu-top-bar-item>
    </div>
  }

  <!-- Elastic gutter: pushes center group away from the left group. -->
  <div class="header-overflow-row__spacer tw-flex-1 tw-min-w-0"></div>

  <!-- CENTER items -->
  <!--
    Rendered after the first spacer so they naturally sit in the middle.
    The More button (if present) follows immediately, also in the center section.
  -->
  @for (item of _visibleCenterItems(); track item._id) {
    <div #itemEl class="header-overflow-row__item-wrapper tw-inline-flex tw-shrink-0 tw-items-center tw-self-stretch">
      <app-menu-top-bar-item
        [item]="item"
        [isActive]="activeMap().get(item._id)"
        [routerPath]="_routerPaths().get(item._id) ?? null"
        [isCasinoOpened]="isCasinoOpened()"
        [isLightTheme]="isLightTheme()"
        [isLoggedIn]="isLoggedIn()"
        [showThemeTooltip]="showThemeTooltip()"
        [hideButtonLightMode]="hideButtonLightMode()"
        [showCrmButton]="showCrmButton()"
        [labelClass]="labelClass()"
        [inSubheader]="inSubheader()"
        (itemClick)="itemClick.emit($event)"
        (themeToggle)="themeToggle.emit()"
      >
        @if (_loginItemIds().has(item._id) && loginTemplate()) {
          <ng-container *ngTemplateOutlet="loginTemplate(); context: { item: item }"></ng-container>
        }
      </app-menu-top-bar-item>
    </div>
  }

  <!--
    More button — only rendered when there are hidden items.
    Placed between the two spacers so it sits in the center section and never
    displaces pinned left/right items. The dropdown menu uses `position: absolute`
    so the parent container must have `overflow: visible` (see SCSS).
  -->
  @if (hiddenItems().length > 0) {
    <div
      class="header-overflow-row__more tw-relative tw-shrink-0 tw-self-stretch tw-flex tw-items-center"
      appClickOutside
      (appClickOutside)="_isMenuOpen.set(false)"
    >
      <div
        class="header-overflow-row__more-trigger tw-flex tw-items-center tw-cursor-pointer tw-whitespace-nowrap tw-self-stretch"
        (click)="_toggleMenu()"
      >
        @if (!isMobile()) {
          <span class="header-overflow-row__more-label">{{ 'GENERAL.MORE' | translate }}</span>
        }
        <mat-icon class="header-overflow-row__more-icon">expand_more</mat-icon>
      </div>

      @if (_isMenuOpen()) {
        <app-header-dropdown-menu
          [items]="hiddenItems()"
          xPosition="after"
          [activeItemIds]="_activeItemIds()"
          [routerPaths]="_routerPaths()"
          [isCasinoOpened]="isCasinoOpened()"
          [isLightTheme]="isLightTheme()"
          [isLoggedIn]="isLoggedIn()"
          [inSubheader]="inSubheader()"
          [hideButtonLightMode]="hideButtonLightMode()"
          [showCrmButton]="showCrmButton()"
          [labelClass]="labelClass()"
          (itemClick)="itemClick.emit($event); _isMenuOpen.set(false)"
          (themeToggle)="themeToggle.emit(); _isMenuOpen.set(false)"
        ></app-header-dropdown-menu>
      }
    </div>
  }

  <!-- Elastic gutter: pushes right group to the end of the row. -->
  <div class="header-overflow-row__spacer tw-flex-1 tw-min-w-0"></div>

  <!-- RIGHT items -->
  @for (item of _visibleRightItems(); track item._id) {
    <div #itemEl class="header-overflow-row__item-wrapper tw-inline-flex tw-shrink-0 tw-items-center tw-self-stretch">
      <app-menu-top-bar-item
        [item]="item"
        [isActive]="activeMap().get(item._id)"
        [routerPath]="_routerPaths().get(item._id) ?? null"
        [isCasinoOpened]="isCasinoOpened()"
        [isLightTheme]="isLightTheme()"
        [isLoggedIn]="isLoggedIn()"
        [showThemeTooltip]="showThemeTooltip()"
        [hideButtonLightMode]="hideButtonLightMode()"
        [showCrmButton]="showCrmButton()"
        [labelClass]="labelClass()"
        [inSubheader]="inSubheader()"
        (itemClick)="itemClick.emit($event)"
        (themeToggle)="themeToggle.emit()"
      >
        @if (_loginItemIds().has(item._id) && loginTemplate()) {
          <ng-container *ngTemplateOutlet="loginTemplate(); context: { item: item }"></ng-container>
        }
      </app-menu-top-bar-item>
    </div>
  }
</div>
